import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { LoginAccountEntity } from '@/api/DTO/login-account/loginAccount.interface';
import { PermissionEnum } from '@/api/enum/common.enum';
import { useLazyGetLoginAccountQuery } from '@/api/login-account/loginAccountApi';
import { useGetUser } from '@/hooks/useGetUser';
import { programAppLoginTabPath } from './ProgramAppLoginTab';

/**
 * After New register / Re-register of a member, or creating a family member,
 * the backend tries to set up the Program App login account. When it could
 * not be activated, prompt the staff and take them to the Program App login
 * tab to finish it.
 */
export function useLoginAccountRegisterPrompt() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const showModal = useModal();
	const { userInfo } = useGetUser();
	const [getLoginAccount] = useLazyGetLoginAccountQuery();

	return async (id: string, entity: LoginAccountEntity = 'member') => {
		// Staff who cannot view the tab are not sent there.
		if (
			!id ||
			!userInfo?.permissionList?.includes(
				PermissionEnum.ViewAdmissionRequestAndMember
			)
		)
			return;

		const response = await getLoginAccount({ entity, id });
		if (!response.data || response.data.data.activated) return;

		showModal((modalProps: any) => (
			<ConfirmationDialog
				{...modalProps}
				title={t('loginAccount.registerNotActivatedTitle')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{entity === 'member'
							? t('loginAccount.registerNotActivatedText')
							: t('loginAccount.registerNotActivatedTextFamily')}
					</Typography>
				}
				cancellationText=''
				confirmationText={t('button.next')}
				confirmationButtonProps={{ variant: 'contained' }}
				onConfirm={() => navigate(programAppLoginTabPath(entity, id))}
			/>
		));
	};
}
